import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fileUrl, fmtBytes, uploadChunked } from "@/lib/upload";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Star, Trash2 } from "lucide-react";

export function PhotoManager({ job, onChanged, readOnly }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(null);
  const [reordering, setReordering] = useState(false);
  const photos = (job.files || []).filter((f) => (f.gallery_id || "main") === "main" && f.is_image).sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const file of files) {
      setUploading({ name: file.name, progress: 0 });
      try {
        await uploadChunked(job.id, file, "main", (progress) => setUploading({ name: file.name, progress }));
      } catch {
        toast.error(`Couldn't upload ${file.name}`);
      }
    }
    setUploading(null);
    onChanged();
  };

  const removePhoto = async (file) => {
    try {
      await api.delete(`/admin/jobs/${job.id}/files/${file.id}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove that photo");
    }
  };

  const setCover = async (file) => {
    try {
      await api.patch(`/jobs/${job.id}/cover`, { file_id: file.id });
      toast.success("Cover photo set");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set cover photo");
    }
  };

  const reorder = async (file, direction) => {
    const idx = photos.findIndex((p) => p.id === file.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= photos.length) return;
    const reordered = [...photos];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    setReordering(true);
    try {
      await Promise.all(reordered.map((p, i) => api.patch(`/admin/jobs/${job.id}/files/${p.id}`, { order: i })));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reorder photos");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid="client-photo-manager">
      <h2 className="font-display text-xl">Photos</h2>
      <p className="text-sm text-slate-500 mt-1">The cover photo is used as the hero image on your property website.</p>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="client-photo-upload-input" />
      <Button type="button" data-testid="client-photo-upload-button" variant="outline" onClick={() => inputRef.current?.click()} disabled={!!uploading || readOnly} className="mt-4 rounded-none border-slate-400">
        {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading {uploading.name} — {uploading.progress}%</> : <><ImagePlus className="h-4 w-4 mr-2" strokeWidth={1.5} />Add Photos</>}
      </Button>

      <div className="mt-5 space-y-2.5" data-testid="client-photo-list">
        {photos.length === 0 && <p className="text-sm text-slate-400">No photos yet — add some above.</p>}
        {photos.map((f, i) => {
          const isCover = job.cover_file_id === f.id;
          return (
            <div key={f.id} data-testid={`client-photo-${f.id}`} className="flex items-center gap-3 border border-[#E7E4DE] rounded-lg p-2.5">
              <div className="flex flex-col text-slate-300">
                <button type="button" data-testid={`client-photo-move-up-${f.id}`} title="Move up" disabled={i === 0 || reordering || readOnly} onClick={() => reorder(f, -1)} className="disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:text-slate-300 hover:text-slate-600"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" data-testid={`client-photo-move-down-${f.id}`} title="Move down" disabled={i === photos.length - 1 || reordering || readOnly} onClick={() => reorder(f, 1)} className="disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:text-slate-300 hover:text-slate-600"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <img src={fileUrl(job.id, f.id)} alt="" className="h-14 w-14 rounded object-cover flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{f.original_filename}</p>
                <p className="text-xs text-slate-400">{fmtBytes(f.size)}</p>
              </div>
              <button type="button" data-testid={`client-photo-cover-${f.id}`} onClick={() => setCover(f)} disabled={readOnly} title="Set as cover photo" className={`disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:text-slate-300 ${isCover ? "text-[#C7A361]" : "text-slate-300 hover:text-[#C7A361]"}`}>
                <Star className="h-4 w-4" fill={isCover ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
              <button type="button" data-testid={`client-photo-delete-${f.id}`} onClick={() => removePhoto(f)} disabled={readOnly} className="text-slate-300 hover:text-red-500 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:text-slate-300"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
